import { BIOTRICITY_SUPPORT_ADDRESS, URL_ERP_PORTAL } from '@/shared/config';
import AccessDenied, { AccessNotAvailable } from '@/pages/access-denied';
import { isWebViewCreateStudy } from '@/shared/lib';
import { jt, t } from 'ttag';
import { ConfirmModal } from '@/shared/ui/components';
import {
  ModalC1cReport,
  ModalEscalatedMessage,
  VerifyEmailModal,
  WhatIsNewModal,
  DeactiveTimeTracking,
  FrontEndMonitoring,
} from '@/widgets/session-modals';
import { onLoadIframe } from './authenticationGate.handler';
import './authenticationGate.scss';
import type { AuthenticationGateProps } from './authenticationGate.types';
import { useAuthenticationGate } from './useAuthenticationGate';

/**
 * Guards the authenticated area and hosts the session-level modals around it. Renders the access-denied page when unauthorized, the access-not-available page when a CardioOne user has no facility, otherwise confirm modals for removed access, changed password, deactivated account, changed event buckets, closed clinic and expired token, plus the release-notes, escalation and verify-email modals, and once data is loaded the children with front-end monitoring, Mercy idle tracking and the hidden ERP portal iframe.
 *
 * @param children - The authenticated content rendered once the session data has loaded.
 * @returns The gate element wrapping the children or the access-denied page.
 */
const AuthenticationGate = ({ children }: AuthenticationGateProps) => {
  const {
    state,
    setState,
    loginFrom,
    isLoginThroughAdminOrSales,
    willShowWhatNewPopup,
    willViewTutorialLater,
    shouldStopTimer,
    onCancelVerifyEmailModal,
    displayWhatNewPopup,
    handleSignOutBySocket,
    handleBackToBiocarePortal,
    handleSignInAgainClick,
    handleOkayDeactivatedAccountModal,
    handleOnlySignOutBiocarePortal,
    handleRedirectToBiocarePortal,
    handleSignOutBiocarePortal,
    meRedux,
  } = useAuthenticationGate();

  /**
   * Renders the release-notes modal only when the user should currently see it. Returns the what-is-new modal when the state flag is set, or when the popup is still pending and the user chose to view the tutorial later, and otherwise nothing.
   *
   * @returns The release-notes modal element, or null.
   */
  const whatIsNewModalRenderer = () => {
    if (state.isOpenWhatNewPopup || (willShowWhatNewPopup && willViewTutorialLater)) {
      return (
        <WhatIsNewModal
          displayWhatNewPopup={displayWhatNewPopup}
          willShowWhatNewPopup={willShowWhatNewPopup}
          zIndex={1003}
        />
      );
    }
    return null;
  };

  const emailJsx = (
    <a className="blue-text" href={`mailto:${BIOTRICITY_SUPPORT_ADDRESS.csMail}`}>
      {BIOTRICITY_SUPPORT_ADDRESS.csMail}
    </a>
  );

  return state.isUnauthorized ? (
    <AccessDenied />
  ) : state.isAccessNotAvailable ? (
    <AccessNotAvailable onGoBack={handleSignOutBiocarePortal} userEmail={meRedux?.email || '--'} />
  ) : (
    <>
      <ConfirmModal
        className="access-removed-modal"
        isOpen={state.isOpenAccessRemovedModal}
        title={t`Access removed`}
        question={t`You've been unassigned from this portal. Please return to the Biocare Home portal to continue using the system.`}
        rightButtonName={t`Back to Biocare Portal`}
        buttonRightStyle="neutral"
        rightButtonClassName="back-to-biocare-portal-btn"
        onClickRightButton={handleBackToBiocarePortal}
      />
      <ConfirmModal
        isOpen={state.isOpenPasswordChangedModal}
        onClickRightButton={() => {
          handleSignOutBySocket();
          setState({ isOpenPasswordChangedModal: false });
        }}
        rightButtonName={t`Okay`}
        title={t`Password changed`}
        question={t`Your password has just been changed. Please sign in with your new one to continue!`}
        buttonRightType="outline"
      />
      <ConfirmModal
        isOpen={state.isOpenDeactivatedAccountModal}
        onClickRightButton={handleOkayDeactivatedAccountModal}
        rightButtonName={t`Got It`}
        buttonRightStyle="danger"
        title={t`Account Deactivated`}
        question={jt`Your account was deactivated. Please contact the Biotricity Support team via the following email ${emailJsx} for further information. Thank you!`}
      />
      <ConfirmModal
        isOpen={state.isOpenEventBucketsChangedModal}
        onClickRightButton={() => {
          window.location.reload();
        }}
        rightButtonName={t`OK`}
        title={t`Event buckets have changed`}
        question={t`The page will be reloaded since there have been some adjustments to the Event Buckets`}
        buttonRightType="outline"
      />
      <ConfirmModal
        isOpen={state.isOpenClosedClinicModal}
        onClickRightButton={() => {
          handleSignOutBySocket();
          setState({ isOpenClosedClinicModal: false });
        }}
        rightButtonName={t`Okay`}
        title={t`Clinic closed`}
        question={jt`Your clinic was closed. Please contact the Biotricity Support team via the following email ${emailJsx} for further information. Thank you!`}
        buttonRightStyle="danger"
      />
      <ConfirmModal
        isOpen={state.isOpenExpiredTokenModal}
        onClickRightButton={handleSignInAgainClick}
        rightButtonName={t`Sign In Again`}
        title={t`Session Expired`}
        question={t`Your session has expired for security reasons. Please sign in again to continue.`}
        isSaving={state.isSigningInAgain}
      />
      {whatIsNewModalRenderer()}
      {!isWebViewCreateStudy() && <ModalEscalatedMessage zIndex={1002} />}
      <ModalC1cReport />
      <VerifyEmailModal
        open={state.isOpenVerifyEmailModal}
        onCancel={onCancelVerifyEmailModal}
        verifyEmailDeadline={state.verifyEmailDeadline}
      />

      {state.isLoadedData && (
        <>
          {children}
          <FrontEndMonitoring hasCheckedLoginStatus={state.hasCheckedLoginStatus} />
          {loginFrom === 'Mercy' && !shouldStopTimer && (
            <DeactiveTimeTracking
              isLoginWithBiocarePortal={state.isLoginWithBiocarePortal}
              handleOnlySignOutBiocarePortal={handleOnlySignOutBiocarePortal}
              handleRedirectToBiocarePortal={handleRedirectToBiocarePortal}
            />
          )}
        </>
      )}
      {isLoginThroughAdminOrSales && (
        <iframe
          id="iframe-id"
          className="erp-portal-iframe"
          src={URL_ERP_PORTAL}
          title="ERP Portal"
          style={{ display: 'none', visibility: 'hidden' }}
          onLoad={onLoadIframe}
        />
      )}
    </>
  );
};

export default AuthenticationGate;
